import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import Offcanvas from "bootstrap/js/dist/offcanvas";
import { useMobile } from "../../hooks/custom/useMobile";
import useTab from "../../hooks/custom/useTab";
import { cmsEnv, headers } from "../../exports/exports";

const SASTApplicationList = () => {
  const [applications, setApplications] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedApplication, setSelectedApplication] = useState<any | null>(
    null,
  );

  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  const mobile = useMobile();
  const tab = useTab();

  const fetchApplications = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_cmsUrl}/content-manager/${process.env.REACT_APP_cmsProjectId}/application-form?pagination[limit]=1000&sort=created_at:desc`,
        {
          headers: {
            ...headers,
            ...cmsEnv,
          },
        },
      );
      setApplications(response.data);
    } catch (error) {
      console.error("Error fetching applications", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApplications();
  }, []);

  const handleSortByDate = () => {
    setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
  };

  /* --------- SORTED DATA --------- */
  const sortedApplications = useMemo(() => {
    return [...applications].sort((a, b) => {
      const dateA = new Date(a.created_at).getTime();
      const dateB = new Date(b.created_at).getTime();
      return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
    });
  }, [applications, sortOrder]);

  useEffect(() => {
    return () => {
      const el = document.getElementById("applicationOffcanvas");
      if (el) {
        const instance = Offcanvas.getInstance(el);
        instance?.dispose();
      }

      document
        .querySelectorAll(".offcanvas-backdrop")
        .forEach((b) => b.remove());
    };
  }, []);

  const openOffcanvas = (app: any) => {
    setSelectedApplication(app);

    const el = document.getElementById("applicationOffcanvas");
    if (!el) return;

    let instance = Offcanvas.getInstance(el);
    if (!instance) {
      instance = new Offcanvas(el);
    }
    instance.show();
  };

  const SortIcon = ({ order }: { order: "asc" | "desc" }) => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 640 640"
      width="16"
      height="16"
      style={{
        marginLeft: "6px",
        transition: "transform 0.2s ease",
        transform: order === "asc" ? "rotate(180deg)" : "rotate(0deg)",
      }}
    >
      <path d="M130.4 268.2C135.4 280.2 147 288 160 288L480 288C492.9 288 504.6 280.2 509.6 268.2C514.6 256.2 511.8 242.5 502.7 233.3L342.7 73.3C330.2 60.8 309.9 60.8 297.4 73.3L137.4 233.3C128.2 242.5 125.5 256.2 130.5 268.2zM130.4 371.7C125.4 383.7 128.2 397.4 137.3 406.6L297.3 566.6C309.8 579.1 330.1 579.1 342.6 566.6L502.6 406.6C511.8 397.4 514.5 383.7 509.5 371.7C504.5 359.7 492.9 352 480 352L160 352C147.1 352 135.4 359.8 130.4 371.8z" />
    </svg>
  );

  const DetailRow = ({
    label,
    value,
  }: {
    label: string;
    value?: React.ReactNode;
  }) => {
    const displayValue =
      value === null || value === undefined || value === "" || value === false
        ? "-"
        : value;
    return (
      <tr>
        {" "}
        <td style={{ fontWeight: 600, width: "40%" }}>{label}</td>{" "}
        <td>{displayValue}</td>{" "}
      </tr>
    );
  };

  const renderFileLink = (fileArray?: [{ media: { url: string } }]) => {
    const url = fileArray?.[0]?.media?.url;

    return url ? (
      <a
        href={`${process.env.REACT_APP_cmsUrl}${url}`}
        target="_blank"
        rel="noreferrer"
      >
        View File
      </a>
    ) : null;
  };

  return (
    <section>
      <div className="td_height_80 td_height_lg_80" />
      <div className="td_height_80 td_height_lg_40" />

      <div className="container">
        <h2 className="text-center mb-4">List of Applications</h2>

        <div style={{ overflowX: "auto" }}>
          <table width="100%" border={1} cellPadding={10}>
            <thead>
              <tr>
                <th>SN</th>
                <th>Full Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Nationality</th>
                <th
                  onClick={handleSortByDate}
                  style={{ cursor: "pointer", userSelect: "none" }}
                >
                  Applied At <SortIcon order={sortOrder} />
                </th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} align="center">
                    Loading...
                  </td>
                </tr>
              ) : (
                sortedApplications.map((app, index) => (
                  <tr key={app.id}>
                    <td>{index + 1}</td>
                    <td>
                      {[app.first_name, app.middle_name, app.last_name]
                        .filter(Boolean)
                        .join(" ")}
                    </td>
                    <td>{app.email}</td>
                    <td>{app.phone || "-"}</td>
                    <td>{app.nationality || "-"}</td>
                    <td>{new Date(app.created_at).toLocaleString()}</td>
                    <td>
                      <button
                        type="button"
                        onClick={() => openOffcanvas(app)}
                        style={{
                          border: "none",
                          background: "transparent",
                          cursor: "pointer",
                          fontSize: "18px",
                        }}
                      >
                        👁
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div
        className={`offcanvas ${
          mobile || tab ? "offcanvas-bottom" : "offcanvas-end"
        }`}
        tabIndex={-1}
        id="applicationOffcanvas"
        data-bs-backdrop="true"
        data-bs-scroll="false"
        style={{ width: mobile || tab ? "100%" : "900px" }}
      >
        <div className="offcanvas-body">
          <button
            type="button"
            className="btn-close mb-3"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
          />

          <h4 className="mb-3">Application Details</h4>
          {selectedApplication ? (
            <div className="table-responsive">
              <table className="table table-sm">
                <tbody>
                  <DetailRow
                    label="Studies"
                    value={selectedApplication.studies}
                  />

                  <DetailRow
                    label="First Priority Program"
                    value={selectedApplication.first_priority_program}
                  />

                  <DetailRow
                    label="Second Priority Program"
                    value={selectedApplication.second_priority_program}
                  />

                  <DetailRow
                    label="Third Priority Program"
                    value={selectedApplication.third_priority_program}
                  />

                  <DetailRow
                    label="Full Name"
                    value={[
                      selectedApplication.first_name,
                      selectedApplication.middle_name,
                      selectedApplication.last_name,
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  />

                  <DetailRow
                    label="Date of Birth"
                    value={
                      selectedApplication.date_of_birth
                        ? new Date(
                            selectedApplication.date_of_birth,
                          ).toLocaleDateString()
                        : null
                    }
                  />

                  <DetailRow
                    label="Gender"
                    value={selectedApplication.gender}
                  />

                  <DetailRow
                    label="Nationality"
                    value={selectedApplication.nationality}
                  />

                  <DetailRow
                    label="Ethnicity"
                    value={selectedApplication.ethnicity}
                  />

                  <DetailRow label="Email" value={selectedApplication.email} />

                  <DetailRow label="Phone" value={selectedApplication.phone} />

                  <DetailRow
                    label="Current Address"
                    value={selectedApplication.current_address}
                  />

                  <DetailRow
                    label="Permanent Address"
                    value={selectedApplication.permanent_address}
                  />

                  <DetailRow
                    label="Guardian Name"
                    value={selectedApplication.guardian_full_name}
                  />

                  <DetailRow
                    label="Guardian Relation"
                    value={selectedApplication.guardian_relation}
                  />

                  <DetailRow
                    label="10+2 Institution"
                    value={selectedApplication["10_2_institution"]}
                  />

                  <DetailRow
                    label="Year of Graduation"
                    value={selectedApplication.year_of_graduation}
                  />

                  <DetailRow
                    label="Finance Plan"
                    value={selectedApplication.education_finance}
                  />

                  <DetailRow
                    label="Scholarship Consideration"
                    value={selectedApplication.consideration_for_scholarship}
                  />

                  <DetailRow
                    label="Internship Organization"
                    value={selectedApplication.internship_organization}
                  />

                  <DetailRow
                    label="Internship Duration (Months)"
                    value={selectedApplication.internship_duration_months}
                  />

                  <DetailRow
                    label="Referee 1"
                    value={[
                      selectedApplication.referee_name_1,
                      selectedApplication.referee_email_1,
                      selectedApplication.referee_contact_1,
                    ]
                      .filter(Boolean)
                      .join(" | ")}
                  />

                  <DetailRow
                    label="Referee 2"
                    value={[
                      selectedApplication.referee_name_2,
                      selectedApplication.referee_email_2,
                      selectedApplication.referee_contact_2,
                    ]
                      .filter(Boolean)
                      .join(" | ")}
                  />

                  <DetailRow
                    label="Other Degrees"
                    value={renderFileLink(selectedApplication.other_degrees)}
                  />

                  <DetailRow
                    label="Awards & Certificates"
                    value={renderFileLink(
                      selectedApplication.awards_and_certificates,
                    )}
                  />

                  <DetailRow
                    label="Government ID"
                    value={renderFileLink(selectedApplication.government_id)}
                  />

                  <DetailRow
                    label="CV"
                    value={renderFileLink(selectedApplication.cv)}
                  />

                  <DetailRow
                    label="Statement of Purpose"
                    value={renderFileLink(selectedApplication.sop)}
                  />

                  <DetailRow
                    label="SEE Certificate"
                    value={renderFileLink(selectedApplication.see_certificate)}
                  />

                  <DetailRow
                    label="10+2 Certificate"
                    value={renderFileLink(
                      selectedApplication["10_2_certificate"],
                    )}
                  />

                  <DetailRow
                    label="Profile Photo"
                    value={renderFileLink(selectedApplication.profile_photo)}
                  />

                  <DetailRow
                    label="Payment Proof"
                    value={renderFileLink(selectedApplication.payment_proof)}
                  />

                  <DetailRow
                    label="Applied At"
                    value={
                      selectedApplication.created_at
                        ? new Date(
                            selectedApplication.created_at,
                          ).toLocaleString()
                        : null
                    }
                  />
                </tbody>
              </table>
            </div>
          ) : (
            <p>No application selected</p>
          )}
        </div>
      </div>

      <div className="td_height_30" />
    </section>
  );
};

export default SASTApplicationList;
